import axios, { AxiosResponse } from "axios";
import { fetchAuthSession } from "@aws-amplify/auth";

/**
 * Field error from the new Core API error format
 */
export type FieldError = {
  field: string;
  reason: string;
  message: string;
};

/**
 * Core API error response format
 */
type ApiErrorResponse = {
  error: {
    code: string;
    message: string;
    status: number;
    details: {
      fields?: FieldError[];
      resource?: string;
      id?: string;
      reason?: string;
      [key: string]: unknown;
    };
  };
};

export class ErrorResponse extends Error {
  constructor(public override message: string, public status: number) {
    super(message);
    this.name = 'ErrorResponse';
  }
}

export class InternalServerError extends ErrorResponse {
  constructor(message: string, status: number) {
    super(message, status);
    this.name = 'InternalServerError';
  }
}

export class NotFoundError extends ErrorResponse {
  public resource?: string;
  public resourceId?: string;

  constructor(message: string, status: number, resource?: string, resourceId?: string) {
    super(message, status);
    this.name = 'NotFoundError';
    this.resource = resource;
    this.resourceId = resourceId;
  }
}

export class Unauthorized extends ErrorResponse {
  constructor(message: string, status: number) {
    super(message, status);
    this.name = 'Unauthorized';
  }
}

export class UnprocessableEntityError {
  public fields: FieldError[];

  constructor(
    public message: string,
    public status: number,
    fields: FieldError[] = []
  ) {
    this.fields = fields;
  }
}

export class ConflictError extends ErrorResponse {
  public resource?: string;
  public reason?: string;
  public details: Record<string, unknown>;

  constructor(
    message: string,
    status: number,
    details: Record<string, unknown> = {}
  ) {
    super(message, status);
    this.name = 'ConflictError';
    this.resource = details.resource as string | undefined;
    this.reason = details.reason as string | undefined;
    this.details = details;
  }
}


export const unauthenticatedApi = axios.create({
  baseURL: import.meta.env.VITE_BACKEND_URL,
});

export const api = axios.create({
  baseURL: import.meta.env.VITE_BACKEND_URL,
});

api.interceptors.request.use(
  async (config) => {
    const { accessToken } = (await fetchAuthSession()).tokens ?? {};

    config.headers["Content-Type"] = "application/json";
    config.headers["Authorization"] = `Bearer ${accessToken}`;

    return config;
  },
  (error) => Promise.reject(error)
);

unauthenticatedApi.interceptors.request.use(
  async (config) => {
    config.headers["Content-Type"] = "application/json";
    return config;
  },
  (error) => Promise.reject(error)
);


[api, unauthenticatedApi].forEach((instance) =>
  instance.interceptors.response.use(
    <T>(response: AxiosResponse<T>) => response,
    (error) => {
      const response = error.response as AxiosResponse<unknown> | undefined;

      if (!response) {
        return Promise.reject(new ErrorResponse(error.message || "Network error", 0));
      }

      const data = response.data as ApiErrorResponse | undefined;

      // A non-enveloped error body (e.g. a 500 HTML page, or any response
      // without `{ error: { code } }`) must not crash the interceptor by
      // destructuring `undefined`. Surface a generic error with the status.
      if (!data || typeof data !== "object" || !data.error) {
        return Promise.reject(
          new ErrorResponse(
            (typeof response.data === "string" && response.data) ||
              error.message ||
              "Unexpected server error",
            response.status
          )
        );
      }

      const { code, message, details } = data.error;

      switch (code) {
        case 'unauthenticated':
          return Promise.reject(new Unauthorized(message, response.status));

        case 'not_found':
          return Promise.reject(
            new NotFoundError(message, response.status, details.resource, details.id)
          );

        case 'validation_failed':
          return Promise.reject(
            new UnprocessableEntityError(message, response.status, details.fields || [])
          );

        case 'conflict':
          return Promise.reject(new ConflictError(message, response.status, details));

        case 'internal_error':
          return Promise.reject(new InternalServerError(message, response.status));

        default:
          return Promise.reject(new ErrorResponse(message, response.status));
      }
    }
  )
);
